import { debounce, IItem, isNullOrUndefined } from '@bimser/core';
import * as React from 'react';
import ReactDOM from 'react-dom';
import BCMenu, { IMenuItemClickEventArgs } from '../BCMenu';
import Styles from './assets/contextMenuStyles.scss';
import {
	IArgumentAboutRightClickEvent,
	IContextMenuClickEventArgs,
	IContextMenuOpenArgs,
	IContextMenuProps
} from './entities';

const BCContextMenu = (props: IContextMenuProps) => {
	const [openKeys, setOpenKeys] = React.useState<Array<string>>([]);
	const contextMenuRefDom = React.useRef<HTMLDivElement>(null);
	const lastClicktedItem = React.useRef<IItem>(null);

	const tiggerMethod = props.triggerMethod || 'context';
	const properDocument = props.popupContainer || document;

	React.useEffect(() => {
		triggerContextMenuPosition();
		return () => {
			properDocument.removeEventListener('click', _handleClick, true);
			properDocument.removeEventListener('scroll', _handleClick, true);
		};
	}, [props.items]);

	React.useEffect(() => {
		triggerContextMenuPosition();
	}, [props.mouseEvent?.clientX, props.mouseEvent?.clientY]);

	const triggerContextMenuPosition = () => {
		if (props.mouseEvent || (props.clientX && props.clientY)) {
			contextMenuPosition();
		}
	};

	const contextMenuPosition = () => {
		const _event: React.MouseEvent = props.mouseEvent;

		let clickX = _event ? _event.clientX : props.clientX;
		let clickY = _event ? _event.clientY : props.clientY;
		clickX = clickX + (props.marginX || 0);
		clickY = clickY + (props.marginY || 0);

		const screenW = window.innerWidth;
		const screenH = window.innerHeight;
		const rootW = contextMenuRefDom.current.offsetWidth;
		const rootH = contextMenuRefDom.current.offsetHeight;

		const right = screenW - clickX > rootW;
		const left = !right;
		const top = screenH - clickY > rootH;
		const bottom = !top;

		contextMenuRefDom.current.style.position = `fixed`;
		contextMenuRefDom.current.style.zIndex = `9999`;

		if (right) {
			contextMenuRefDom.current.style.left = `${clickX + 5}px`;
		}

		if (left) {
			contextMenuRefDom.current.style.left = `${clickX - rootW - 5}px`;
		}

		if (top) {
			contextMenuRefDom.current.style.top = `${clickY + 5}px`;
		}

		if (bottom) {
			contextMenuRefDom.current.style.top = `${clickY - rootH - 5}px`;
			if (clickY - rootH < 0) {
				contextMenuRefDom.current.style.top = `5px`;
				contextMenuRefDom.current.style.maxHeight = `${clickY - 5}px`;
				contextMenuRefDom.current.style.overflowY = 'scroll';
			}
		}

		properDocument.addEventListener('click', _handleClick, true);
		properDocument.addEventListener('scroll', _handleClick, true);
	};

	const _handleClick = debounce((event: any) => {
		const isEventOutOfMenu = !contextMenuRefDom.current?.contains?.(event.target);
		const isEventScroll = event.type == 'scroll';
		const isClickEvent = event.type == 'click';
		const isScrollAndOutOfMenu = isEventOutOfMenu && isEventScroll && props.closeWhenAnyScroll;
		const shouldCloseContextMenu =
			isEventOutOfMenu ||
			isNullOrUndefined(props.keepOpenParents) ||
			!props.keepOpenParents ||
			!lastClicktedItem.current?.items?.length;

		if (shouldCloseContextMenu && props.visible) {
			if (
				(isClickEvent && (tiggerMethod !== 'click' || isEventOutOfMenu)) ||
				isScrollAndOutOfMenu
			) {
				props.onClose?.(false);
				properDocument.removeEventListener('click', _handleClick, true);
				properDocument.removeEventListener('scroll', _handleClick, true);
				if (props.useStateForOpenKeys) {
					setOpenKeys([]);
				}
			}
		}
	}, 30);

	const onClick = (args: IMenuItemClickEventArgs) => {
		args.domEvent?.stopPropagation();
		args.domEvent?.preventDefault();
		lastClicktedItem.current = args.data;
		const shouldCloseContextMenu =
			isNullOrUndefined(props.keepOpenParents) ||
			!props.keepOpenParents ||
			!args.data?.items?.length;

		props.onClick?.({ sender: args.domEvent, data: args.data, parentNode: props.parentNode });
		if (props.useStateForOpenKeys && shouldCloseContextMenu) {
			setOpenKeys([]);
		}
		if (shouldCloseContextMenu) {
			props.onClose?.(false);
		}
	};

	const onOpenChange = (openKeys: string[]) => {
		if (props.useStateForOpenKeys) {
			setOpenKeys(openKeys);
		}
		props.onOpenChange?.(openKeys);
	};

	const ContextMenu = (
		<div
			style={props.style}
			className={Styles.contextMenu + ' ' + (props.className ? props.className : '')}
			ref={contextMenuRefDom}
		>
			{props.visible && (
				<BCMenu
					style={props.menuStyle}
					isLoading={props.isLoading ? props.isLoading : false}
					forceNativeClick={props.forceNativeClick}
					forceSubMenuRender={props.forceSubMenuRender}
					mode="vertical"
					items={props.items}
					key="ContextMenu"
					onClick={onClick}
					popupClassName={Styles.subMenuContextMenu}
					onOpenChange={onOpenChange}
					openKeys={props.useStateForOpenKeys ? openKeys : props.openKeys}
				/>
			)}
		</div>
	);

	if (props.popupContainer) {
		return ReactDOM.createPortal(ContextMenu, props.popupContainer);
	}

	return ContextMenu;
};
export default BCContextMenu;
export {
	IArgumentAboutRightClickEvent,
	IContextMenuClickEventArgs,
	IContextMenuOpenArgs,
	IContextMenuProps
};
